import React = require("react");
import * as dateMath from 'date-arithmetic';
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import { AgendaMeetingActivityListing, rng } from "../../../amorg-common";
import { createActivity, getAgendaMeetingActivities, loadMeetingTemplate } from "../../../api/activityClientApi";
import { Button } from "../../../designSystem/button/Button";
import { ActionPointSummaryCounterCompositeIcon } from "../../../designSystem/compositeComponents/compositeIcons/ActionPointCounterCompositeIcon";
import { AgendaItemCounterCompositeIcon } from "../../../designSystem/compositeComponents/compositeIcons/AgendaItemCounterCompositeIcon";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Icon, iconName } from "../../../designSystem/icons/Icons";
import { OnOff } from "../../../designSystem/inputs/OnOff";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Modal } from "../../../designSystem/Modal";
import { PageHeader } from "../../../designSystem/PageHeader";
import { PageSection } from "../../../designSystem/PageSection";
import { PageSectionTable, PageSectionRow, PageSectionCell } from "../../../designSystem/PageSectionTable";
import { TooltipTheOneWrapper } from "../../../designSystem/TooltipTheOne";
import { P, Span12, Span16 } from "../../../designSystem/typography/Body";
import { txt } from "../../../txt";
import { toDateString, toTimeString } from "../../../utils/date";
import { SingleColumnLayout } from "../../_layouts/SingleColumnLayout";


type State = {
	loading:boolean,
	activities:AgendaMeetingActivityListing[],
    redirectId:string
}

type AgendaMeetingListingViewProps = {
    type: 'WorkgroupMeeting' | 'AnnualWorkgroupStatus',
    
    generalActivityHeaderText:string,
    generalActivityHeaderIcon?:iconName,

    currentActivitiesHeaderText:string,
    currentActivitiesHeaderIcon?:iconName,
    
    createNewButtonText:string,
    
    pastActivitiesHeaderText:string,
    pastActivitiesHeaderIcon?:iconName
}


export function AgendaMeetingListingView(props:AgendaMeetingListingViewProps): JSX.Element
{
	const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });
    const [showAnnualStatusCreateModal, setShowAnnualStatusCreateModal] = React.useState(false);

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getAgendaMeetingActivities(props.type);
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    function handleClickNavigate(activityId:string)
    {
        setState({...state, redirectId:activityId});
    }

    async function handleClickNew()
    {
        if(props.type === 'WorkgroupMeeting'){
            handleCreateNewMeeting();
        }
        else if(props.type === 'AnnualWorkgroupStatus'){
            setShowAnnualStatusCreateModal(true);
        }
    }

    async function handleCreateNewMeeting()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()+1, 0, 0, 0);
        const endDate = dateMath.add(startDate, 1, 'hours');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            props.type,
            txt.getDynamic(`NewActivityType_${props.type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    async function handleCreateNewAnnualStatus(useTemplate:boolean)
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()+1, 0, 0, 0);
        const endDate = dateMath.add(startDate, 1, 'hours');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            props.type,
            txt.getDynamic(`NewActivityType_${props.type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            if(useTemplate){
                const loadResult = await loadMeetingTemplate(activityId, 'annual-meeting-standard');
                if(!loadResult.isSuccess){
                    setState({...state, loading:false});
                    return;
                }
            }
            setState({...state, redirectId:activityId});
            setShowAnnualStatusCreateModal(false);
        }
    }

    const current = state.activities
        .filter(activity => activity.start >= new Date())
        .sort((a, b) => a.start.getTime() - b.start.getTime());
    const past = state.activities
        .filter(activity => activity.start < new Date())
        .sort((a, b) => b.start.getTime() - a.start.getTime());

    const redirectUrl = props.type === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  (state.redirectId || '')
        : '/annualworkgroupstatus/' + (state.redirectId || '')

    return (

        <>
        <PageHeader 
            headerIcon={props.generalActivityHeaderIcon}
            headerText={props.generalActivityHeaderText}
            buttons={<Button variant="filled" icon="actions_plus" text={props.createNewButtonText} onClick={handleClickNew} size={"42px"} emphasize />}
        />

        <SingleColumnLayout>
            {state.redirectId !== '' &&
                <Navigate to={redirectUrl} />
            }

            <LoadingPageOverlay loading={state.loading} />

            <PageSection
                header={{
                    title: props.currentActivitiesHeaderText,
                    icon: props.currentActivitiesHeaderIcon
                }}
                headerStyleVariant='minimal'
                contentStyleVariant='minimal'
                contentPadding="16px 0">
    
                <PageSectionTable
                    headerColumns={[
                        {text:'Dato', width:'160px'},
                        {text:'Titel'},
                        {text:'Lokation'},
                        {text:'Agenda', width:'200px'},
                        {text:'Deltagere', width:'200px'}
                    ]}
                    noContentText='Ingen aktuelle møder'
                    showAllLimit={5}
                >
                    {current.map(activity => {
                        const participantTooltipContent = activity.participants && activity.participants.length > 0
                            ? (
                                <>
                                    {activity.participants.map((participant, index) => (
                                        <div key={`${participant}-${index}`}>{participant}</div>
                                    ))}
                                </>
                            )
                            : 'Ingen inviterede';

                        return (
                            <PageSectionRow key={activity.id} onClick={() => handleClickNavigate(activity.id)}>
                                <PageSectionCell><FormattedDateTime start={activity.start} end={activity.end} /></PageSectionCell>
                                <PageSectionCell><ActivityFocus title={activity.title}><TruncatedText>{activity.title}</TruncatedText></ActivityFocus></PageSectionCell>
                                <PageSectionCell>
                                    <Flex gap={4}>
                                        <Icon name="interface_glyph_location" />
                                        <TruncatedText>
                                            {activity.location ? activity.location : 'Ingen lokation'}
                                        </TruncatedText>
                                    </Flex>
                                </PageSectionCell>
                                <PageSectionCell><AgendaItemCounterCompositeIcon agendaitemCount={activity.agendaItemCount} /></PageSectionCell>
                                <PageSectionCell>
                                    <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                                        <Flex flexDirection="row" alignItems="center" gap={8}>
                                            <Icon name="interface_user_team" />
                                            {activity.participantCount !== undefined ? activity.participantCount : 'Ingen inviterede'}
                                        </Flex>
                                    </TooltipTheOneWrapper>
                                </PageSectionCell>
                            </PageSectionRow>
                        );
                    })}
                </PageSectionTable>
                        
            </PageSection>
            
            {past.length > 0 &&
                <>
                <PageSection
                    header={{
                        title: props.pastActivitiesHeaderText,
                        icon: props.pastActivitiesHeaderIcon
                    }}
                    headerStyleVariant='minimal'
                    contentStyleVariant='minimal'
                    contentPadding="16px 0"
                >

                    <PageSectionTable
                        headerColumns={[
                            {text:'Dato', width:'160px'},
                            {text:'Titel'},
                            {text:'Handling', width:'200px'},
                            {text:'Deltagere', width:'200px'}
                        ]}
                        noContentText='Ingen tidligere møder'
                        showAllLimit={5}
                    >
                        {past.map(activity => {
                            const participantTooltipContent = activity.participants && activity.participants.length > 0
                            ? (
                                <>
                                    {activity.participants.map((participant, index) => (
                                        <div key={`${participant}-${index}`}>{participant}</div>
                                    ))}
                                </>
                            )
                            : 'Ingen deltagere';

                            return (
                                <PageSectionRow key={activity.id} onClick={() => handleClickNavigate(activity.id)}>
                                    <PageSectionCell>
                                        <Flex flexDirection="row" alignItems="center" gap={4}>
                                            <FormattedDateTime start={activity.start} end={activity.end} />
                                        </Flex>
                                    </PageSectionCell>
                                    <PageSectionCell><ActivityFocus title={activity.title}><TruncatedText>{activity.title}</TruncatedText></ActivityFocus></PageSectionCell>
                                    <PageSectionCell><ActionPointSummaryCounterCompositeIcon {...activity} /></PageSectionCell>
                                    <PageSectionCell>
                                        <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                                            <Flex flexDirection="row" alignItems="center" gap={8}>
                                                <Icon name="interface_user_team" />
                                                {activity.participantCount !== undefined ? activity.participantCount : 'Ingen deltagere'}
                                            </Flex>
                                        </TooltipTheOneWrapper>
                                    </PageSectionCell>
                                </PageSectionRow>
                            );
                        })}
                    </PageSectionTable>
                </PageSection>

                </>
                }

                <AnnualStatusCreateModal
                    show={showAnnualStatusCreateModal}
                    onClose={() => setShowAnnualStatusCreateModal(false)}
                    onCreate={handleCreateNewAnnualStatus}
                />
            
            
        </SingleColumnLayout>
    </>
	);
}


function AnnualStatusCreateModal(props: { show: boolean; onCreate:(useTemplate:boolean) => void; onClose: () => void; }) {

    const [useTemplate, setUseTemplate] = React.useState(true);

    return (
        <Modal showModal={props.show} onCancel={props.onClose}
            title="Opret ny årlig drøftelse"
            content={
                <Flex flexDirection="column" gap={16}>
                    <P>Skal drøftelsen oprettes udfra den anbefalede mødeskabelon ?</P>
                    <P>Skabelonen er vejledende, og mødet kan frit tilpasses efter jeres behov.</P>

                    <Flex justifyContent="flex-start" gap={8}>
                        <OnOff active={useTemplate} onClick={setUseTemplate} />
                        <span>Anvend skabelon</span>
                    </Flex>

                    <Flex justifyContent="flex-end" gap={8}>
                        <Button text="Opret ny årlig drøftelse" variant={"filled"} onClick={() => { props.onCreate(useTemplate); } } />
                        <Button text="Annuller" variant="outlined" onClick={props.onClose} />
                    </Flex>
                </Flex>
            }
        />
    );
}

function FormattedDateTime(props:{ start:Date, end:Date}) {
    return (
        <Flex flexDirection="column" alignItems="flex-start">
            <Span16>{toDateString(props.start)}</Span16>
            <Span12 color="gray" fontWeight={400}>{toTimeString(props.start)}-{toTimeString(props.end)}</Span12>
        </Flex>
    );
}

const ActivityRow = styled.div`
    align-items: center;
    background: ${props => props.theme.colors.activityCard.background};
    display: grid;
    grid-template-columns: 5% 28% 17% 28% 9% minmax(0, 1fr);
    gap: 16px;
    border-bottom: 1px solid ${props => props.theme.colors.activityCard.border};
    padding: 0 8px 6px 8px;
    white-space: nowrap;  
`;

export const ActivityRowHeader = styled(ActivityRow)`
    background: ${props => props.theme.colors.activityCard.headerBackground};
    color: ${props => props.theme.colors.activityCard.headerColor};
    padding: 8px;
    font-weight: bold;
    white-space: nowrap;
    border-bottom: 0;
`;

export const ActivityPastRow = styled.div`
    align-items: center;
    background: ${props => props.theme.colors.activityCard.background};
    display: grid;
    grid-template-columns: 17% 52% 7% 9% minmax(0, 1fr);
    gap: 16px;
    border-bottom: 1px solid ${props => props.theme.colors.activityCard.border};
    padding: 0 8px 6px 8px;
    white-space: nowrap;
`;


export const ActivityPastRowHeader = styled(ActivityPastRow)`
    background: ${props => props.theme.colors.activityCard.headerBackground};
    color: ${props => props.theme.colors.activityCard.headerColor};
    padding: 8px;
    font-weight: bold;
    white-space: nowrap;
    border-bottom: 0;
`;


const ActivityFocus = styled.div`
font-size: 16px;
    color: ${props => props.theme.colors.text.normal};
    padding: 4px 8px;
    display: flex;
    align-items: center;
    flex-direction: row;     
    min-width: 0;
`;

const TruncatedText = styled.span`
    min-width: 0;
    overflow: hidden;
    flex: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
`;